import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'typed-redux-saga/macro';

import {
  LOG_AUDIT_EVENT,
  SAVE_SUCCESS,
  SET_REDACTION_STATUS_FAILURE,
  SET_REDACTION_STATUS_REQUEST,
  SET_REDACTION_STATUS_SUCCESS,
} from '../actions';
import { serviceUpdateTdoRedactionStatus } from '../services';
import { selectRedactionStatus, selectSession } from '../selectors';
import {
  selectRedactionCustomStatuses,
  selectRedactionStatusSettings,
} from '@redact-modules/redactionStatuses/selectors';
import {
  REDACTION_STATUS_DRAFT,
  REDACTION_STATUS_LABEL_DRAFT,
  REDACTION_STATUS_TO_DO,
  isRedactionStatusEnabled,
  isWellKnownRedactionStatusToDo,
  resolveRedactionStatusDisplayLabel,
} from '@common-modules/mediaDetails/redactionStatuses';
import { selectToken, selectGraphQLConfig } from './auth';
import { actionUpdateRedactionStatusChange } from '@redact-modules/casemedia/actions';
import { isRedactionStatus } from '@redact-modules/casemedia/models';
import { selectEnableFolderView } from '@common/user-onboarding/selectors';
import { TDOId } from '@common-modules/universal/models/Brands';

export function* onSetRedactionStatusRequest({
  payload: {
    status,
    statusLabel,
    statusKey,
    previousStatus,
    previousStatusName,
  },
}: ReturnType<typeof SET_REDACTION_STATUS_REQUEST>) {
  const session = yield* select(selectSession);
  if (!session) {
    return;
  }
  try {
    const token: string = yield* select(selectToken);
    const cfg = yield* select(selectGraphQLConfig);
    yield* call(
      serviceUpdateTdoRedactionStatus,
      session.tdoId,
      {
        redactionStatus: statusLabel,
        ...(statusKey ? { redactionStatusKey: statusKey } : {}),
      },
      token,
      cfg.apiRoot,
      cfg.graphQLEndpoint,
      cfg.veritoneAppId
    );
    yield* put(
      SET_REDACTION_STATUS_SUCCESS({
        status,
        statusLabel,
        statusKey,
        tdoId: String(session.tdoId),
      })
    );
    /**
     * VE-28429: tell the folder view, which the video/AV editor has always done
     * (mediaDetails saga, on FETCH_STATUS_CHANGE_SUCCESS) and this route never
     * did. Its rows carry their own copy of the redaction status, and nothing
     * else updates it: the targeted refresh of the file just closed deliberately
     * does NOT read `status` or `details` back, because re-reading them can undo
     * the folder view's own optimistic writes (see fetchStalenessAssetsQuery).
     *
     * Until this ticket the omission was invisible — closing a document
     * refetched the whole request, which reloaded the row along with everything
     * else. Reusing a recently loaded list instead makes it visible, so the
     * sync has to be explicit.
     *
     * The identity, not the display name: `redactionStatusKey` for a custom
     * status, otherwise the well-known value (see the payload's own docs).
     */
    const identity = statusKey || status;
    const isFolderView = yield* select(selectEnableFolderView);
    if (isFolderView && isRedactionStatus(identity)) {
      yield* put(
        actionUpdateRedactionStatusChange({
          tdoId: String(session.tdoId) as TDOId,
          redactionStatus: identity,
          redactionStatusLabel: statusLabel,
          ...(statusKey ? { redactionStatusKey: statusKey } : {}),
        })
      );
    }
    // Same audit line the video/AV editor writes (see mediaDetails
    // saga/audit-logs), word for word, so one file's trail reads the same
    // whichever editor changed it — this route had never written one at all.
    // After the mutation, not before it: an audit trail should record what
    // happened, and the video path's pre-persistence emit is the older
    // pattern, not the one to copy.
    //
    // The human-readable name, never the identity: for a custom status that
    // is a UUID, which is what VE-27621 took back out of the video trail. The
    // picker freezes the name it displayed onto `statusLabel`, so it wins;
    // programmatic transitions (the To Do → Draft auto-promote) resolve
    // against the org's statuses instead.
    const customStatuses = yield* select(selectRedactionCustomStatuses);
    yield* put(
      LOG_AUDIT_EVENT({
        action: `Redaction Status updated to: ${
          statusLabel?.trim() ||
          resolveRedactionStatusDisplayLabel(status, customStatuses)
        }`,
      })
    );
  } catch (e) {
    const message =
      e instanceof Error ? e.message : 'Failed to update redaction status';
    console.warn(
      '[documents] redaction status update failed; rolling back.',
      e
    );
    yield* put(
      SET_REDACTION_STATUS_FAILURE({
        previousStatus,
        previousStatusName,
        message,
        tdoId: String(session.tdoId),
      })
    );
  }
}

/**
 * Auto-promote `To Do → Draft` on the first successful save (VE-21914).
 *
 * Product behavior: a brand-new TDO opens at `To Do`, and the moment the
 * user makes any change that actually persists, status advances to `Draft`
 * so the dashboard can distinguish "touched" from "untouched" documents
 * without requiring users to remember to change it manually.
 *
 * After this fires once, `session.redactionStatus` is `'draft'` and the
 * condition no longer matches — so subsequent saves don't re-issue the
 * mutation. We treat a null/unset status the same as `To Do` (the chip
 * displays that as the default).
 */
function* onSaveSuccessMaybePromoteStatus({
  payload,
}: ReturnType<typeof SAVE_SUCCESS>) {
  // VE-28061: same reason as the audit emitter — a detached save for another
  // document must not advance the OPEN document's status to Draft.
  const session = yield* select(selectSession);
  if (session?.tdoId !== payload.tdoId) {
    return;
  }
  const current = yield* select(selectRedactionStatus);
  // Custom statuses must not collapse to "to do" via normalize — only the
  // real well-known To Do status auto-promotes to Draft (VE-24546).
  if (!isWellKnownRedactionStatusToDo(current ?? REDACTION_STATUS_TO_DO)) {
    return;
  }
  // Respect the org's status settings: if Draft is disabled in this org we
  // can't auto-promote into it — doing so would leave the TDO in a status
  // that isn't in the user's enabled menu, breaking parity with the video
  // flow (which gates on `isRedactionStatusEnabled(DRAFT, settings)` —
  // see `src/common-app/state/modules/mediaDetails/saga/saga.ts`).
  const settings = yield* select(selectRedactionStatusSettings);
  if (!isRedactionStatusEnabled(REDACTION_STATUS_DRAFT, settings)) {
    return;
  }
  yield* put(
    SET_REDACTION_STATUS_REQUEST({
      status: REDACTION_STATUS_DRAFT,
      statusLabel: REDACTION_STATUS_LABEL_DRAFT,
      // Draft is a built-in — no key, and To Do (the value we roll back to) is
      // built-in too, so no frozen name is needed.
      previousStatus: current,
      previousStatusName: null,
      // VE-26730: auto-promote is app-initiated — history flags it isSystem.
      isSystemTransition: true,
    })
  );
}

export function* redactionStatusSaga() {
  yield* all([
    takeLatest(SET_REDACTION_STATUS_REQUEST, onSetRedactionStatusRequest),
    takeEvery(SAVE_SUCCESS, onSaveSuccessMaybePromoteStatus),
  ]);
}
